import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import styled from 'styled-components';

import { ChatMessage } from 'services/api/chat';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

type Props = {
  message: ChatMessage;
  // Own messages align right with a tinted bubble; others align left with a gray bubble.
  isOwn: boolean;
};

// Matches the mockup's "Today 9:04 AM" (no "at"); relative for today/yesterday, else dated.
function formatMessageTime(iso: string): string {
  const date = parseISO(iso);
  if (isNaN(date.getTime())) return '';
  const days = differenceInCalendarDays(date, new Date());
  if (days === 0) return format(date, "'Today' h:mm a");
  if (days === -1) return format(date, "'Yesterday' h:mm a");
  return format(date, 'MM/dd/yyyy h:mm a');
}

// One message: a sender + timestamp header above a bubble. v1 renders `plaintext` as text —
// never raw markup/HTML — so mention tokens display as plain text until OLIO-2837.
export default function MessageBubble({ message, isOwn }: Props) {
  return (
    <Row $own={isOwn} data-testid='chat-message'>
      <Meta $own={isOwn}>
        <Sender>{message.createdBy.name}</Sender>
        <Timestamp>{formatMessageTime(message.createdAt)}</Timestamp>
      </Meta>
      <Bubble $own={isOwn}>{message.plaintext}</Bubble>
    </Row>
  );
}

const Row = styled.div<{ $own: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $own }) => ($own ? 'flex-end' : 'flex-start')};
  gap: 4px;
  max-width: 100%;
`;

const Meta = styled.div<{ $own: boolean }>`
  display: flex;
  flex-direction: ${({ $own }) => ($own ? 'row-reverse' : 'row')};
  align-items: baseline;
  gap: 8px;
`;

const Sender = styled(BodySmallBold)`
  color: ${colors.black100};
`;

const Timestamp = styled(BodySmall)`
  color: ${colors.black50};
`;

const Bubble = styled(BodySmall)<{ $own: boolean }>`
  max-width: 76%;
  padding: 10px 14px;
  border-radius: 12px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${colors.black100};
  background: ${({ $own }) => ($own ? colors.blue10 : colors.black5)};
`;
